import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getCampaignSummary } from '../utils/campaignAI';
import { useAuth } from '../context/AuthContext';
import SubscriptionBadge from '../hooks/SubscriptionBadge';
import TrialExpiredModal from '../components/TrialExpiredModal';
import logger from '../utils/logger';

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    connectedAccounts: 0,
    totalCampaigns: 0,
    problematicCampaigns: 0,
    totalWaste: 0,
    champions: 0,
    critical: 0,
    avgConfidence: 0,
    loading: true
  });
  
  // Trial state management - ADD THIS
  const [isTrialExpired, setIsTrialExpired] = useState(false);
  const [trialDaysOverdue, setTrialDaysOverdue] = useState(0);
  
  // Use AuthContext for consistent authentication
  const { user, isAuthenticated, loading: authLoading, logout, getAuthDebugInfo, testBackendConnection } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showDebug, setShowDebug] = useState(false);
  const [backendTestResult, setBackendTestResult] = useState(null);
  const navigate = useNavigate();

  // Enhanced authentication check
  useEffect(() => {
    logger.info('🔍 Dashboard Auth Check:', {
      authLoading,
      isAuthenticated,
      userEmail: user?.email,
      userId: user?.id
    });

    if (authLoading) {
      logger.info('⏳ AuthContext still loading, waiting...');
      return;
    }

    if (!isAuthenticated || !user) {
      logger.info('❌ User not authenticated in Dashboard after auth loading complete');
      
      setTimeout(() => {
        if (!isAuthenticated || !user) {
          logger.info('🚫 Still not authenticated after delay, redirecting to login');
          navigate('/login', { replace: true });
        }
      }, 1000);
      return;
    }
    
    logger.info('✅ User authenticated in Dashboard:', user?.email);
    fetchDashboardStats();
    checkTrialStatus(); // ADD THIS - Check trial status when user is authenticated
    
  }, [isAuthenticated, authLoading, user, navigate]);

 // FIXED - Check trial status (respects Pro users)
  const checkTrialStatus = async () => {
    try {
      logger.info('🔄 Checking trial status for user:', user?.id);
      
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      if (!token) {
        logger.info('❌ No access token for trial check');
        return;
      }

      // FIRST: Check if user is Pro in the profiles table
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('plan_type, trial_end_date')
        .eq('id', user?.id)
        .single();

      if (profile) {
        logger.info('📋 Profile data:', profile);
        
        // If user is Pro, NO trial check needed - exit immediately
        if (profile.plan_type === 'pro') {
          logger.info('✅ User is Pro - skipping trial expiration check');
          setIsTrialExpired(false);
          setTrialDaysOverdue(0);
          return;
        }

        // User is on free_trial - check expiration
        if (profile.trial_end_date) {
          const trialEnd = new Date(profile.trial_end_date);
          const today = new Date();
          const daysOverdue = Math.floor((today - trialEnd) / (1000 * 60 * 60 * 24));
          
          if (daysOverdue > 0) {
            setIsTrialExpired(true);
            setTrialDaysOverdue(daysOverdue);
            logger.info(`❌ Trial expired ${daysOverdue} days ago`);
          } else {
            setIsTrialExpired(false);
            setTrialDaysOverdue(0);
            logger.info(`✅ Trial active - ${Math.abs(daysOverdue)} days remaining`);
          }
          return;
        }
      }

      // Fallback to backend check
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';
      
      const trialResponse = await fetch(`${backendUrl}/api/auth/subscription`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (trialResponse.ok) {
        const subscriptionData = await trialResponse.json();
        
        // Check if Pro from backend
        if (subscriptionData.plan_type === 'pro') {
          logger.info('✅ Backend confirms Pro status');
          setIsTrialExpired(false);
          setTrialDaysOverdue(0);
          return;
        }
        
        // Check trial expiration from backend
        if (subscriptionData.trial_end_date) {
          const trialEnd = new Date(subscriptionData.trial_end_date);
          const today = new Date();
          const daysOverdue = Math.floor((today - trialEnd) / (1000 * 60 * 60 * 24));
          
          if (daysOverdue > 0) {
            setIsTrialExpired(true);
            setTrialDaysOverdue(daysOverdue);
            logger.info(`❌ Trial expired ${daysOverdue} days ago`);
          } else {
            setIsTrialExpired(false);
            logger.info('✅ Trial is still active');
          }
        }
      } else {
        logger.info('⚠️ Could not fetch subscription data from backend');
        // DO NOT use fallback date check - it doesn't know about Pro status
        // Just assume trial is valid if we can't verify
        setIsTrialExpired(false);
      }
    } catch (error) {
      logger.error('❌ Trial status check error:', error);
      // On error, don't block the user
      setIsTrialExpired(false);
    }
  };

  // Mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Fetch dashboard stats
  const fetchDashboardStats = async () => {
    try {
      logger.info('🔄 Fetching dashboard stats for user:', user?.id);
      
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      if (!token) {
        logger.info('❌ No access token found');
        setDashboardData(prev => ({ ...prev, loading: false }));
        return;
      }

      logger.info('🔄 Fetching dashboard stats with token');

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';
      
      const campaignsResponse = await fetch(`${backendUrl}/api/auth/campaigns-simple`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (campaignsResponse.ok) {
        const campaignsData = await campaignsResponse.json();
        const summary = getCampaignSummary(campaignsData);
        
        logger.info('✅ Dashboard stats loaded successfully');
        
        setDashboardData({
          connectedAccounts: summary.connectedAccounts,
          totalCampaigns: summary.totalCampaigns,
          problematicCampaigns: summary.problematicCampaigns,
          totalWaste: summary.totalWaste,
          champions: summary.champions,
          critical: summary.critical,
          avgConfidence: summary.avgConfidence,
          loading: false
        });
      } else {
        logger.info('⚠️ Failed to fetch campaigns, using default stats');
        setDashboardData({
          connectedAccounts: 0,
          totalCampaigns: 0,
          problematicCampaigns: 0,
          totalWaste: 0,
          champions: 0,
          critical: 0,
          avgConfidence: 0,
          loading: false
        });
      }
    } catch (error) {
      logger.error('❌ Dashboard stats error:', error);
      setDashboardData(prev => ({ ...prev, loading: false }));
    }
  };

  // Logout handler
  const handleLogout = async () => {
    try {
      logger.info('🔄 Logging out...');
      await logout();
      logger.info('✅ Logout successful, redirecting...');
      navigate('/login', { replace: true });
    } catch (error) {
      logger.error('❌ Logout error:', error);
      navigate('/login', { replace: true });
    }
  };

  const handleConnectAccounts = () => {
    navigate('/integrations');
  };

  // Test backend connection
  const handleTestBackend = async () => {
    logger.info('🧪 Testing backend connection from Dashboard...');
    setBackendTestResult({ testing: true });
    
    const result = await testBackendConnection();
    setBackendTestResult(result);
    
    if (result.success) {
      alert('✅ Backend connection successful!\n\nThe server is reachable and responding correctly.');
    } else {
      const errorMsg = result.isCORSIssue 
        ? `❌ CORS Error Detected!\n\nFrontend: ${window.location.origin}\nBackend: ${process.env.REACT_APP_BACKEND_URL}\n\nThe backend needs to allow requests from your frontend domain.`
        : `❌ Backend Connection Failed!\n\nError: ${result.error}\n\nPlease check if the backend server is running.`;
      
      alert(errorMsg);
    }
  };

  // Loading states
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-900 to-blue-900 text-white">
        <div className="text-center">
          <div className="text-4xl mb-4">🕵️</div>
          <div className="text-xl">Authenticating Detective...</div>
          <div className="text-sm text-gray-400 mt-2">Loading your session...</div>
        </div>
      </div>
    );
  }

  if (!authLoading && (!isAuthenticated || !user)) {
    logger.info('🚫 Not authenticated after loading complete, should redirect');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-900 to-blue-900 text-white">
        <div className="text-center">
          <div className="text-4xl mb-4">🔐</div>
          <div className="text-xl">Redirecting to login...</div>
          <div className="text-sm text-gray-400 mt-2">Authentication required</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-900 via-gray-900 to-blue-900 relative overflow-hidden">
      {/* Top Navigation Bar */}
      <nav className="bg-black/90 backdrop-blur-md border-b border-white/10 py-3 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex items-center gap-2 cursor-pointer">
              <span className="text-2xl lg:text-3xl animate-skull-glow">🕵🏽</span>
              <span className="text-lg lg:text-xl font-bold text-brand-red">WhyDidItDie</span>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Subscription Widget */}
            <SubscriptionBadge />

            {!isMobile && (
              <div className="flex flex-col items-end text-white">
                <span className="text-xs text-brand-green font-semibold">Welcome back, Detective!</span>
                <span className="text-sm text-gray-300">{user?.email || 'Loading...'}</span>
              </div>
            )}
            
            <button 
              className="flex items-center gap-2 bg-brand-red/10 text-brand-red border border-brand-red/30 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 hover:bg-brand-red/20 min-w-fit"
              onClick={handleLogout}
            >
              <span className="text-base">🚪</span>
              {!isMobile && 'Logout'}
            </button>
          </div>
        </div>
      </nav>

      {/* Debug Panel */}
      {showDebug && (
        <div className="bg-blue-500/10 border border-blue-500/30 p-4 m-4 rounded-lg text-blue-400 text-sm z-50">
          <h3 className="mb-4 font-semibold">🔧 Debug Information</h3>
          {(() => {
            const debugInfo = getAuthDebugInfo();
            return (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
                <div>Auth Status: {debugInfo.isAuthenticated ? '✅ Authenticated' : '❌ Not Authenticated'}</div>
                <div>User: {debugInfo.userEmail || 'None'}</div>
                <div>Loading: {debugInfo.isLoading ? 'Yes' : 'No'}</div>
                <div>Has Token: {debugInfo.hasToken ? 'Yes' : 'No'}</div>
                <div>Stored Token: {debugInfo.storedToken ? 'Yes' : 'No'}</div>
                <div>Stored User: {debugInfo.storedUser ? 'Yes' : 'No'}</div>
                <div>Backend: {debugInfo.backendUrl}</div>
                <div>Frontend: {debugInfo.currentOrigin}</div>
                <div>CORS Issue: {debugInfo.corsIssue ? '⚠️ Likely' : '✅ Unlikely'}</div>
                {/* ADD TRIAL STATUS TO DEBUG INFO */}
                <div>Trial Expired: {isTrialExpired ? `❌ Yes (${trialDaysOverdue} days)` : '✅ No'}</div>
              </div>
            );
          })()}
          
          <div className="flex gap-4 flex-wrap">
            <button 
              onClick={handleTestBackend}
              className="bg-blue-500/20 border border-blue-500/50 text-blue-400 px-4 py-2 rounded-md cursor-pointer text-sm disabled:opacity-50"
              disabled={backendTestResult?.testing}
            >
              {backendTestResult?.testing ? '🔄 Testing...' : '🧪 Test Backend'}
            </button>
            
            <button 
              onClick={() => {
                localStorage.clear();
                sessionStorage.clear();
                window.location.reload();
              }}
              className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-2 rounded-md cursor-pointer text-sm"
            >
              🗑️ Clear All Data
            </button>
          </div>
          
          {backendTestResult && !backendTestResult.testing && (
            <div className={`mt-4 p-2 rounded-md border ${
              backendTestResult.success 
                ? 'bg-green-500/10 border-green-500/30 text-green-400' 
                : 'bg-red-500/10 border-red-500/30 text-red-400'
            }`}>
              Backend Test: {backendTestResult.success ? '✅ Success' : `❌ Failed - ${backendTestResult.error}`}
              {backendTestResult.isCORSIssue && (
                <div className="text-xs mt-1">⚠️ This appears to be a CORS issue</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Detective Animation Background - Hidden on mobile */}
      {!isMobile && (
        <div className="detective-background">
          <div className="detective-container">
            <div className="detective-element text-4xl top-[10%] left-[5%] animate-detective-walk z-[2]">🕵️</div>
            <div className="detective-element text-2xl top-[15%] left-[8%] animate-magnify-search z-[3]">🔍</div>
            <div className="detective-element text-lg top-[30%] left-[20%] animate-evidence-float opacity-60">📊</div>
            <div className="detective-element text-lg top-[60%] right-[25%] opacity-60" style={{animation: 'evidenceFloat 5s ease-in-out infinite 1s'}}>💸</div>
            <div className="detective-element text-lg bottom-[25%] left-[15%] opacity-60" style={{animation: 'evidenceFloat 6s ease-in-out infinite 2s'}}>📈</div>
            <div className="detective-element text-sm top-[25%] left-[12%] opacity-40" style={{animation: 'footprintAppear 2s ease-in-out infinite 0.5s'}}>👣</div>
            <div className="detective-element text-sm top-[35%] left-[18%] opacity-40" style={{animation: 'footprintAppear 2s ease-in-out infinite 1s'}}>👣</div>
            <div className="flashlight"></div>
          </div>
          <div className="scanning-line"></div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex items-start justify-center p-2 lg:p-4 pt-4 lg:pt-8">
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-4 lg:p-8 max-w-7xl w-full text-white relative z-10">
          {/* Header */}
          <div className="text-center mb-4 lg:mb-8">
            <div className="flex items-center justify-center gap-3 mb-2 flex-wrap">
              <span className="text-3xl lg:text-4xl animate-skull-glow">🕵🏽</span>
              <h1 className="text-2xl lg:text-4xl font-black text-gradient-red">WhyDidItDie</h1>
            </div>
            <p className="text-sm lg:text-lg text-gray-300 mb-2 max-w-2xl mx-auto leading-relaxed">
              AI powered marketing that finds budget leaks, predicts problems, and optimizes marketing performance
            </p>
            <div className="text-xs lg:text-sm text-brand-green font-semibold animate-tagline-glow">
              🔍 Detect • 🔮 Predict • ⚡ Prevent • 💰 Save Millions
            </div>
          </div>
          
          {/* Quick Launch */}
          <div className="mb-4 lg:mb-6 p-3 lg:p-4 bg-white/[0.02] border border-white/10 rounded-xl">
            <div className="text-center mb-3">
              <h4 className="text-base lg:text-xl font-bold text-brand-yellow mb-1">⚡ Quick Launch</h4>
              <p className="text-xs lg:text-sm text-gray-400">Jump into your favorite detective tools</p>
            </div>
            <div className={`grid gap-2 lg:gap-3 ${isMobile ? 'grid-cols-2' : 'grid-cols-4'}`}>
              <button 
                className="flex flex-col items-center gap-2 p-2 lg:p-3 border-none rounded-lg cursor-pointer transition-all duration-300 text-white text-xs lg:text-sm font-semibold min-h-[60px] lg:min-h-[70px] justify-center bg-gradient-to-br from-purple-500 to-purple-700 hover:from-purple-600 hover:to-purple-800 transform hover:scale-105"
                onClick={() => navigate('/campaign-detective')}
              >
                <span className="text-lg lg:text-xl mb-1">📊</span>
                <span className="text-center leading-tight">Campaign Intelligence</span>
              </button>
              <button 
                className="flex flex-col items-center gap-2 p-2 lg:p-3 border-none rounded-lg cursor-pointer transition-all duration-300 text-white text-xs lg:text-sm font-semibold min-h-[60px] lg:min-h-[70px] justify-center bg-gradient-to-br from-orange-500 to-orange-700 hover:from-orange-600 hover:to-orange-800 transform hover:scale-105"
                onClick={() => navigate('/waste-hunter')}
              >
                <span className="text-lg lg:text-xl mb-1">🔍</span>
                <span className="text-center leading-tight">Budget Waste Hunter</span>
              </button>
              <button 
                className="flex flex-col items-center gap-2 p-2 lg:p-3 border-none rounded-lg cursor-pointer transition-all duration-300 text-white text-xs lg:text-sm font-semibold min-h-[60px] lg:min-h-[70px] justify-center bg-gradient-to-br from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 transform hover:scale-105"
                onClick={() => navigate('/content-engine')}
              >
                <span className="text-lg lg:text-xl mb-1">🚀</span>
                <span className="text-center leading-tight">AI Content Engine</span>
              </button>
              <button 
                className="flex flex-col items-center gap-2 p-2 lg:p-3 border-none rounded-lg cursor-pointer transition-all duration-300 text-white text-xs lg:text-sm font-semibold min-h-[60px] lg:min-h-[70px] justify-center bg-gradient-to-br from-green-500 to-green-700 hover:from-green-600 hover:to-green-800 transform hover:scale-105"
                onClick={() => navigate('/market-intelligence-engine')}
              >
                <span className="text-lg lg:text-xl mb-1">🧠</span>
                <span className="text-center leading-tight">Marketing Intelligence</span>
              </button>
            </div>
          </div>
          
          {/* Module Grid */}
          <div className={`gap-4 lg:gap-6 mt-4 lg:mt-8 ${isMobile ? 'grid grid-cols-1' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
            {/* Campaign Archaeology */}
            <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 lg:p-6 text-center transition-all duration-300 cursor-pointer relative overflow-hidden hover:bg-white/[0.05] hover:border-white/20 hover:transform hover:scale-105">
              <div className="flex items-center justify-center gap-3 mb-4 flex-wrap">
                <span className="text-xl lg:text-2xl">📊</span>
                <h3 className="text-base lg:text-lg font-semibold">Campaign Detective</h3>
              </div>
              <p className="text-xs lg:text-sm text-gray-300 mb-4 lg:mb-6 leading-relaxed min-h-[2rem] lg:min-h-[2.8rem] flex items-center justify-center">
                Smart campaign performance analysis and actionable insights
              </p>
              <button 
                className="bg-gradient-to-r from-brand-red to-brand-red-dark text-white border-none py-2 lg:py-3 px-4 lg:px-6 rounded-lg cursor-pointer text-xs lg:text-sm font-semibold transition-all duration-300 w-full hover:from-brand-red-dark hover:to-red-600 transform hover:scale-105"
                onClick={() => navigate('/campaign-detective')}
              >
                Analyze Campaigns
              </button>
            </div>
            
            {/* Decision Tracker */}
            <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 lg:p-6 text-center transition-all duration-300 cursor-pointer relative overflow-hidden hover:bg-white/[0.05] hover:border-white/20 hover:transform hover:scale-105">
              <div className="flex items-center justify-center gap-3 mb-4 flex-wrap">
                <span className="text-xl lg:text-2xl">🕵️</span>
                <h3 className="text-base lg:text-lg font-semibold">Decision Tracker</h3>
              </div>
              <p className="text-xs lg:text-sm text-gray-300 mb-4 lg:mb-6 leading-relaxed min-h-[2rem] lg:min-h-[2.8rem] flex items-center justify-center">
                Who made what decisions and when
              </p>
              <button 
                className="bg-gradient-to-r from-brand-red to-brand-red-dark text-white border-none py-2 lg:py-3 px-4 lg:px-6 rounded-lg cursor-pointer text-xs lg:text-sm font-semibold transition-all duration-300 w-full hover:from-brand-red-dark hover:to-red-600 transform hover:scale-105"
                onClick={() => navigate('/decision-tracker')}
              >
                View Decisions
              </button>
            </div>
            
            {/* AI Content Engine */}
            <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 lg:p-6 text-center transition-all duration-300 cursor-pointer relative overflow-hidden hover:bg-white/[0.05] hover:border-white/20 hover:transform hover:scale-105">
              <div className="flex items-center justify-center gap-3 mb-4 flex-wrap">
                <span className="text-xl lg:text-2xl">🤖</span>
                <h3 className="text-base lg:text-lg font-semibold">AI Content Engine</h3>
              </div>
              <p className="text-xs lg:text-sm text-gray-300 mb-4 lg:mb-6 leading-relaxed min-h-[2rem] lg:min-h-[2.8rem] flex items-center justify-center">
                AI powered content engine using customer psychology and optimization
              </p>
              <button 
                className="bg-gradient-to-r from-brand-red to-brand-red-dark text-white border-none py-2 lg:py-3 px-4 lg:px-6 rounded-lg cursor-pointer text-xs lg:text-sm font-semibold transition-all duration-300 w-full hover:from-brand-red-dark hover:to-red-600 transform hover:scale-105"
                onClick={() => navigate('/content-engine')}
              >
                Create and Optimize Content
              </button>
            </div>
            
            {/* Waste Detective */}
            <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 lg:p-6 text-center transition-all duration-300 cursor-pointer relative overflow-hidden hover:bg-white/[0.05] hover:border-white/20 hover:transform hover:scale-105">
              <div className="flex items-center justify-center gap-3 mb-4 flex-wrap">
                <span className="text-xl lg:text-2xl">🔍</span>
                <h3 className="text-base lg:text-lg font-semibold">Waste Detective</h3>
              </div>
              <p className="text-xs lg:text-sm text-gray-300 mb-4 lg:mb-6 leading-relaxed min-h-[2rem] lg:min-h-[2.8rem] flex items-center justify-center">
                Advanced AI forensics to detect hidden budget leaks
              </p>
              <button 
                className="bg-gradient-to-r from-brand-red to-brand-red-dark text-white border-none py-2 lg:py-3 px-4 lg:px-6 rounded-lg cursor-pointer text-xs lg:text-sm font-semibold transition-all duration-300 w-full hover:from-brand-red-dark hover:to-red-600 transform hover:scale-105"
                onClick={() => navigate('/waste-hunter')}
              >
                Hunt Waste
              </button>
            </div>

            {/* Intelligence Engine */}
            <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 lg:p-6 text-center transition-all duration-300 cursor-pointer relative overflow-hidden hover:bg-white/[0.05] hover:border-white/20 hover:transform hover:scale-105">
              <div className="flex items-center justify-center gap-3 mb-4 flex-wrap">
                <span className="text-xl lg:text-2xl">🧠</span>
                <h3 className="text-base lg:text-lg font-semibold">Market Intelligence Engine</h3>
                <span className="text-xs text-brand-green-light font-bold bg-brand-green-light/10 px-2 py-1 rounded-xl ml-2 animate-badge-pulse">NEW</span>
              </div>
              <p className="text-xs lg:text-sm text-gray-300 mb-4 lg:mb-6 leading-relaxed min-h-[2rem] lg:min-h-[2.8rem] flex items-center justify-center">
                Global market research and competitor insights for entering markets at any stage
              </p>
              <button 
                className="bg-gradient-to-r from-brand-red to-brand-red-dark text-white border-none py-2 lg:py-3 px-4 lg:px-6 rounded-lg cursor-pointer text-xs lg:text-sm font-semibold transition-all duration-300 w-full hover:from-brand-red-dark hover:to-red-600 transform hover:scale-105"
                onClick={() => navigate('/market-intelligence-engine')}
              >
                Explore Markets
              </button>
            </div>

            {/* Platform Intelligence */}
            <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 lg:p-6 text-center transition-all duration-300 cursor-pointer relative overflow-hidden hover:bg-white/[0.05] hover:border-white/20 hover:transform hover:scale-105">
              <div className="flex items-center justify-center gap-3 mb-4 flex-wrap">
                <span className="text-xl lg:text-2xl">🎯</span>
                <h3 className="text-base lg:text-lg font-semibold">Platform Intelligence</h3>
              </div>
              <p className="text-xs lg:text-sm text-gray-300 mb-4 lg:mb-6 leading-relaxed min-h-[2rem] lg:min-h-[2.8rem] flex items-center justify-center">
                Marketing briefs, GTM strategies, and smart platform recommendations
              </p>
              <button 
                className="bg-gradient-to-r from-brand-red to-brand-red-dark text-white border-none py-2 lg:py-3 px-4 lg:px-6 rounded-lg cursor-pointer text-xs lg:text-sm font-semibold transition-all duration-300 w-full hover:from-brand-red-dark hover:to-red-600 transform hover:scale-105"
                onClick={() => navigate('/platform-intelligence')}
              >
                Build GTM Strategy
              </button>
            </div>
          </div>

          {/* Getting Started Section */}
          <div className="mt-6 lg:mt-8 p-6 lg:p-8 bg-brand-green/5 border border-brand-green/20 rounded-xl text-center">
            <div className="text-4xl lg:text-5xl mb-4 animate-detective-bounce">🕵️</div>
            <h4 className="text-lg lg:text-xl mb-2 text-brand-green-light font-semibold">Ready to Start the Investigation?</h4>
            <p className="text-xs lg:text-sm text-gray-300 mb-4 lg:mb-6 leading-relaxed">
              Connect your advertising accounts to begin AI-powered campaign forensics
            </p>
            <div className="flex justify-center">
              <button 
                className="flex items-center gap-3 bg-gradient-to-r from-brand-green-light to-brand-green text-white border-none py-3 lg:py-4 px-8 lg:px-10 rounded-xl cursor-pointer text-sm lg:text-lg font-bold transition-all duration-300 shadow-lg shadow-brand-green/40 min-w-[200px] lg:min-w-[280px] justify-center hover:from-brand-green hover:to-green-700 transform hover:scale-105"
                onClick={handleConnectAccounts}
              >
                <span className="text-lg lg:text-xl">🔗</span>
                Connect Advertising Accounts
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Trial Expired Modal */}
      {isTrialExpired && (
        <TrialExpiredModal daysOverdue={trialDaysOverdue} />
      )}
    </div>
  );
};

export default Dashboard;